<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import type { GameDateTime } from '../../../common/services/GameTimeService';
import { inferEntityType } from '../../../common/utils/EntityUtils';
import type { EntityTickUpdateData, GameEntity, GameUpdateData } from '../types';
import EntityDetail from './EntityDetail.vue';
import EntityTable from './EntityTable.vue';

const props = defineProps<{
  gameId: string;
  data: GameUpdateData;
  entityTickData: EntityTickUpdateData | null;
}>();

const emit = defineEmits<{
  pause: [];
  resume: [];
  step: [];
  subscribeEntity: [entityId: number];
  unsubscribeEntity: [];
  setGameTime: [time: GameDateTime];
}>();

const selectedEntityId = ref<number | null>(null);

/**
 * Build entity view from tick data when available, otherwise fall back to polling data.
 */
const selectedEntity = computed<GameEntity | null>(() => {
  if (selectedEntityId.value === null) return null;

  // Prefer per-tick data if available and matches selected entity
  if (
    props.entityTickData &&
    props.entityTickData.entityId === selectedEntityId.value &&
    props.entityTickData.components !== null
  ) {
    // Build a GameEntity-like object from tick data
    return {
      id: props.entityTickData.entityId,
      type: inferEntityType(props.entityTickData.components),
      components: props.entityTickData.components,
    };
  }

  // Fall back to polling data
  return props.data.entities.find((e) => e.id === selectedEntityId.value) || null;
});

/** Current tick number from per-tick updates. */
const currentTick = computed(() => props.entityTickData?.tick ?? null);

/** Whether entity was destroyed (tick data exists but components is null). */
const entityDestroyed = computed(
  () =>
    props.entityTickData &&
    props.entityTickData.entityId === selectedEntityId.value &&
    props.entityTickData.components === null,
);

/** Format uptime as HH:MM:SS (hours can exceed 24). */
const formattedUptime = computed(() => {
  const totalSeconds = Math.floor(props.data.uptime);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

/** Format game time simply. */
const formattedGameTime = computed(() => {
  if (!props.data.gameTime) return 'Unknown';
  const { year, month, day, hour, minute } = props.data.gameTime;
  const hh = hour.toString().padStart(2, '0');
  const mm = minute.toString().padStart(2, '0');
  return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hh}:${mm}`;
});

const isTimeModalOpen = ref(false);
const editableTime = ref<GameDateTime>({
  year: 1500,
  month: 1,
  day: 1,
  hour: 12,
  minute: 0,
});

function openTimeModal() {
  if (props.data.gameTime) {
    // Copy the struct
    editableTime.value = { ...props.data.gameTime };
  }
  isTimeModalOpen.value = true;
}

function saveTimeModal() {
  isTimeModalOpen.value = false;
  emit('setGameTime', { ...editableTime.value });
}

function closeTimeModal() {
  isTimeModalOpen.value = false;
}

function selectEntity(id: number) {
  selectedEntityId.value = id;
  emit('subscribeEntity', id);
}

function clearSelection() {
  emit('unsubscribeEntity');
  selectedEntityId.value = null;
}

// Clean up subscription when component unmounts or game changes
watch(
  () => props.gameId,
  () => {
    if (selectedEntityId.value !== null) {
      emit('unsubscribeEntity');
      selectedEntityId.value = null;
    }
  },
);
</script>

<template>
  <div id="view-detail">
    <h2>Game: {{ gameId }}</h2>

    <div class="controls">
      <button
        v-if="!data.isPaused"
        class="btn"
        @click="emit('pause')"
      >
        ⏸ Pause
      </button>
      <button
        v-else
        class="btn"
        @click="emit('resume')"
      >
        ▶ Resume
      </button>
      <button
        class="btn"
        :disabled="!data.isPaused"
        @click="emit('step')"
      >
        ⏭ Next Tick
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-box">
        <div>Uptime</div>
        <div class="stat-value">
          {{ formattedUptime }}
        </div>
      </div>
      <div class="stat-box">
        <div>Players</div>
        <div class="stat-value">
          {{ data.playerCount }}
        </div>
      </div>
      <div class="stat-box">
        <div>Entities</div>
        <div class="stat-value">
          {{ data.entityCount }}
        </div>
      </div>
      <div class="stat-box">
        <div>Tick</div>
        <div class="stat-value">
          {{ data.tickCount }}
        </div>
      </div>
      <div
        class="stat-box"
        style="display: flex; flex-direction: column; justify-content: center; align-items: center; position: relative;"
      >
        <div>In-Game Time</div>
        <div class="stat-value">
          {{ formattedGameTime }}
        </div>
        <button
          class="btn"
          style="position: absolute; right: 5px; top: 5px; padding: 2px 6px; font-size: 12px;"
          @click="openTimeModal"
        >
          Edit
        </button>
      </div>
      <div class="stat-box">
        <div>Status</div>
        <div
          class="stat-value"
          :style="{ color: data.isPaused ? '#f44336' : '#4caf50' }"
        >
          {{ data.isPaused ? 'Paused' : 'Running' }}
        </div>
      </div>
    </div>

    <div v-if="selectedEntityId === null">
      <div class="card">
        <h3>Players</h3>
        <ul v-if="data.players.length > 0">
          <li
            v-for="p in data.players"
            :key="p.id"
            class="player-item"
            @click="selectEntity(p.entityId)"
          >
            <strong>ID {{ p.id }}</strong> (Entity {{ p.entityId }}): {{ p.name }}
          </li>
        </ul>
        <div
          v-else
          style="color: #666; padding: 10px"
        >
          No players connected
        </div>
      </div>

      <div class="card">
        <h3>Entities</h3>
        <EntityTable
          :entities="data.entities"
          @select-entity="selectEntity"
        />
      </div>
    </div>

    <div v-else>
      <div class="card">
        <EntityDetail
          v-if="selectedEntity && !entityDestroyed"
          :entity="selectedEntity"
          :entities="data.entities"
          :tick="currentTick"
          @back="clearSelection"
        />
        <div v-else>
          <p>Entity not found (it may have been destroyed).</p>
          <button
            class="btn"
            @click="clearSelection"
          >
            Back to List
          </button>
        </div>
      </div>
    </div>

    <!-- Time Update Modal -->
    <div
      v-if="isTimeModalOpen"
      class="modal-overlay"
      @click.self="closeTimeModal"
    >
      <div class="modal">
        <h3>Set In-Game Time</h3>
        <div class="form-group row">
          <label>Year: <input
            v-model.number="editableTime.year"
            type="number"
            min="1"
          ></label>
          <label>Month: <input
            v-model.number="editableTime.month"
            type="number"
            min="1"
            max="12"
          ></label>
          <label>Day: <input
            v-model.number="editableTime.day"
            type="number"
            min="1"
            max="31"
          ></label>
        </div>
        <div class="form-group row">
          <label>Hour: <input
            v-model.number="editableTime.hour"
            type="number"
            min="0"
            max="23"
          ></label>
          <label>Minute: <input
            v-model.number="editableTime.minute"
            type="number"
            min="0"
            max="59"
          ></label>
        </div>
        <div class="modal-actions">
          <button
            class="btn cancel-btn"
            @click="closeTimeModal"
          >
            Cancel
          </button>
          <button
            class="btn save-btn"
            @click="saveTimeModal"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.controls {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.controls .btn {
  padding: 8px 16px;
  font-size: 14px;
}

.controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.player-item {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.player-item:hover {
  background-color: #333;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  background-color: #242424;
  padding: 20px;
  border-radius: 8px;
  min-width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #444;
}

.modal h3 {
  margin-top: 0;
  margin-bottom: 20px;
}

.form-group.row {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.form-group label {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 5px;
}

.form-group input {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #555;
  background-color: #1a1a1a;
  color: #fff;
  width: 60px;
}

.form-group input:focus {
  outline: none;
  border-color: #646cff;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  background-color: transparent;
  border: 1px solid #555;
}

.cancel-btn:hover {
  border-color: #888;
}

.save-btn {
  background-color: #4caf50;
  border-color: #4caf50;
  color: white;
}

.save-btn:hover {
  background-color: #45a049;
}
</style>
